import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { adminApi } from "@/admin/api";
import { api as consumerApi } from "@/lib/api";
import { useQuery as useTeamsQuery } from "@tanstack/react-query";
import { toast } from "sonner";

const ROLE_OPTIONS = ["penalties", "corners", "free_kicks", "indirect"];

export default function AdminSetPieces() {
  const qc = useQueryClient();
  const { data: rows } = useQuery({
    queryKey: ["admin-set-pieces"],
    queryFn: async () => (await adminApi.get("/set-piece-takers")).data,
    staleTime: 30_000,
  });
  const { data: boot } = useTeamsQuery({
    queryKey: ["boot-for-admin"],
    queryFn: async () => (await consumerApi.get("/bootstrap")).data,
    staleTime: 5 * 60_000,
  });
  const [form, setForm] = useState({ team_id: "", player_id: "", role: "penalties", priority_order: 1 });

  const create = useMutation({
    mutationFn: async (body) => (await adminApi.post("/set-piece-takers", body)).data,
    onSuccess: () => {
      toast.success("Added set-piece taker");
      setForm({ team_id: "", player_id: "", role: "penalties", priority_order: 1 });
      qc.invalidateQueries({ queryKey: ["admin-set-pieces"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Create failed"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await adminApi.delete(`/set-piece-takers/${id}`)).data,
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-set-pieces"] });
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.team_id || !form.player_id) {
      toast.error("Team and player required");
      return;
    }
    create.mutate({
      team_id: Number(form.team_id),
      player_id: Number(form.player_id),
      role: form.role,
      priority_order: Number(form.priority_order),
    });
  };

  const teams = boot?.teams || [];
  const teamById = (id) => teams.find((t) => t.id === id)?.short_name || `T${id}`;

  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <div className="mono uppercase text-[11px] tracking-widest text-totem-textDim">Editorial</div>
        <h1 className="display-heading text-3xl">Set-piece takers</h1>
      </div>

      <form onSubmit={submit} className="totem-card p-4 grid grid-cols-1 sm:grid-cols-5 gap-2">
        <select
          data-testid="sp-team"
          value={form.team_id}
          onChange={(e) => setForm({ ...form, team_id: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm"
        >
          <option value="">Team…</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input
          data-testid="sp-player"
          placeholder="Player ID"
          type="number"
          value={form.player_id}
          onChange={(e) => setForm({ ...form, player_id: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm mono"
        />
        <select
          data-testid="sp-role"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm"
        >
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input
          data-testid="sp-priority"
          placeholder="Priority 1-5"
          type="number"
          min={1}
          max={5}
          value={form.priority_order}
          onChange={(e) => setForm({ ...form, priority_order: e.target.value })}
          className="bg-totem-surface border border-totem-border rounded px-2 py-2 text-sm mono"
        />
        <button
          type="submit"
          data-testid="sp-submit"
          disabled={create.isPending}
          className="bg-totem-green text-totem-bg font-semibold rounded px-3 py-2 text-sm flex items-center justify-center gap-1"
        >
          <Plus size={14} /> Add
        </button>
      </form>

      <div className="totem-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-totem-surface">
            <tr>
              <th className="text-left px-3 py-2 label-caps">Team</th>
              <th className="text-left px-3 py-2 label-caps">Player ID</th>
              <th className="text-left px-3 py-2 label-caps">Role</th>
              <th className="text-left px-3 py-2 label-caps">Priority</th>
              <th className="text-right px-3 py-2 label-caps">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(rows?.results || []).length === 0 && (
              <tr><td colSpan={5} className="p-4 text-center text-totem-textDim">No set-piece takers curated yet.</td></tr>
            )}
            {(rows?.results || []).map((r) => (
              <tr key={r.id} className="border-t border-totem-borderSoft">
                <td className="px-3 py-2">{teamById(r.team_id)}</td>
                <td className="px-3 py-2 mono">{r.player_id}</td>
                <td className="px-3 py-2 mono uppercase text-xs">{r.role}</td>
                <td className="px-3 py-2 mono">#{r.priority_order}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => del.mutate(r.id)}
                    className="text-totem-red hover:brightness-110 inline-flex items-center gap-1 text-xs mono"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
